/**
 * Counts the pages of a PDF entirely in the browser using pdf.js — no
 * backend call needed. Lazily imported so the (fairly large) pdf.js bundle
 * is only fetched when someone actually uploads a PDF.
 *
 * Vite note: pdf.js needs its worker script served as a real asset. The
 * `new URL(..., import.meta.url)` pattern below is Vite's supported way to
 * get a correct, hashed URL for a file inside node_modules at build time —
 * see https://vitejs.dev/guide/assets.html#new-url-url-import-meta-url
 */
export async function getPdfPageCount(file: File): Promise<number> {
  const pdfjsLib = await import('pdfjs-dist');

  // Only needs to be set once per app lifetime, but assigning it again is
  // harmless — cheaper than threading a "has this run yet" flag around.
  const workerUrl = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });

  try {
    const pdf = await loadingTask.promise;
    const pageCount = pdf.numPages;
    await pdf.destroy();
    return pageCount;
  } catch (error) {
    // Encrypted, corrupted, or otherwise unreadable PDFs land here — the
    // caller falls back to asking the person to enter the count manually.
    throw new Error(
      error instanceof Error ? `Could not read PDF: ${error.message}` : 'Could not read this PDF file.',
    );
  }
}
